import React, { useMemo } from 'react';
import { weightUri } from '../lifecycle';
import { EmptyState, Field, inputStyle, tokens } from './ui/controls';

export type RegistryVersion = {
  version: string;
  s3_uri?: string | null;
  tags?: Record<string, unknown>;
  aliases?: string[];
};

export type RegistryModel = {
  name: string;
  task_type?: string;
  architecture?: string;
  versions: RegistryVersion[];
};

export type RegistryChoice = {
  label: string;
  s3_uri: string;
  detail: string;
};

function stringTags(tags: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(tags || {})) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

export function registryChoices(models: RegistryModel[]): RegistryChoice[] {
  const choices: RegistryChoice[] = [];
  for (const model of models) {
    for (const version of model.versions || []) {
      const direct = typeof version.s3_uri === 'string' ? version.s3_uri : '';
      const uri = direct.endsWith('.pt') ? direct : weightUri(model.name, stringTags(version.tags));
      if (!uri.endsWith('.pt')) continue;
      const aliases = (version.aliases || []).join(', ');
      const detail = [model.task_type, model.architecture, aliases].filter(Boolean).join(' · ');
      choices.push({
        label: `${model.name} v${version.version}`,
        s3_uri: uri,
        detail,
      });
    }
  }
  return choices;
}

export function pickStyle(active: boolean): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '0.4rem 0.65rem',
    borderRadius: tokens.radius,
    border: `1px solid ${active ? tokens.primary : tokens.border}`,
    background: active ? tokens.primarySoft : tokens.surface,
    color: tokens.text,
    cursor: 'pointer',
    fontSize: '0.85rem',
    wordBreak: 'break-all',
  };
}

export function MatchList({ empty, children }: { empty: string; children: React.ReactNode[] }) {
  if (children.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <div style={{ display: 'grid', gap: '0.35rem', maxHeight: 220, overflow: 'auto' }}>
      {children}
    </div>
  );
}

export function RegistryModelSearch({
  choices,
  error,
  query,
  onQuery,
  selectedUri,
  selectedUris,
  onSelect,
  placeholder,
}: {
  choices: RegistryChoice[];
  error: string;
  query: string;
  onQuery: (value: string) => void;
  selectedUri?: string;
  selectedUris?: string[];
  onSelect: (choice: RegistryChoice) => void;
  placeholder: string;
}) {
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = needle
      ? choices.filter((choice) => `${choice.label} ${choice.detail} ${choice.s3_uri}`.toLowerCase().includes(needle))
      : choices;
    return rows.slice(0, 40);
  }, [choices, query]);

  return (
    <div style={{ display: 'grid', gap: '0.45rem' }}>
      <Field label="Search registry">
        <input
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      </Field>
      {error ? (
        <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{error}</span>
      ) : (
        <MatchList empty="No registered .pt weights match that search.">
          {matches.map((choice) => (
            <button
              key={`${choice.label}:${choice.s3_uri}`}
              type="button"
              onClick={() => onSelect(choice)}
              style={pickStyle(choice.s3_uri === selectedUri || Boolean(selectedUris?.includes(choice.s3_uri)))}
            >
              {choice.label}
              {choice.detail ? ` · ${choice.detail}` : ''}
            </button>
          ))}
        </MatchList>
      )}
    </div>
  );
}
